import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { AlertController } from '@ionic/angular/standalone';

/** Components that can report unsaved edits for {@link createIonicUnsavedChangesGuard}. */
export interface HasUnsavedChanges {
  hasUnsavedChanges(): boolean;
}

export interface IonicUnsavedChangesAlertCopy {
  header?: string;
  message?: string;
  stayText?: string;
  discardText?: string;
}

/**
 * Reusable `canDeactivate` guard: Ionic alert when {@link HasUnsavedChanges.hasUnsavedChanges} is true.
 * Returns `true` when the user chooses to leave (discard); `false` when they stay or dismiss.
 */
export function createIonicUnsavedChangesGuard(
  copy?: IonicUnsavedChangesAlertCopy,
): CanDeactivateFn<HasUnsavedChanges> {
  return async (component) => {
    if (!component?.hasUnsavedChanges?.()) {
      return true;
    }
    const alertController = inject(AlertController);
    const alert = await alertController.create({
      header: copy?.header ?? 'Discard changes?',
      message:
        copy?.message ?? 'You have unsaved changes. Leave without saving?',
      buttons: [
        { text: copy?.stayText ?? 'Stay', role: 'cancel' },
        { text: copy?.discardText ?? 'Leave', role: 'confirm' },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    return role === 'confirm';
  };
}

export const worklistSettingsUnsavedGuard = createIonicUnsavedChangesGuard({
  header: 'Discard changes?',
  message: 'You have worklist settings that are not applied. Leave without applying?',
  stayText: 'Stay',
  discardText: 'Leave',
});
